import { useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/common";
import { FilterSelect } from "@/components/BookingFilters";
import { AuditEntry } from "@/components/HistoryDialog";
import { useFetch } from "@/hooks/useFetch";
import { AUDIT_ACTIONS } from "@/lib/constants";

export default function AuditLogPage() {
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ q: "", user_id: "", action: "", date_from: "", date_to: "" });
  const { data: users } = useFetch("/users");
  const params = useMemo(() => Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), [filters]);
  const { data, loading } = useFetch("/audit", params);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const t = setTimeout(() => set("q", q), 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div data-testid="audit-page">
      <PageHeader title="Audit log" subtitle="Who changed what, and when. Read-only – entries cannot be edited or deleted." />
      <div className="mb-4 grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:flex sm:flex-wrap sm:items-center">
        <div className="relative col-span-2 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job number or customer…" className="h-9 bg-slate-50 pl-9" data-testid="audit-search" />
        </div>
        <FilterSelect id="audit-user" placeholder="Staff member" value={filters.user_id} options={(users || []).map((u) => ({ value: u.id, label: u.name }))} onChange={(v) => set("user_id", v)} />
        <FilterSelect id="audit-action" placeholder="Type of change" value={filters.action} options={AUDIT_ACTIONS} onChange={(v) => set("action", v)} />
        <Input type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} className="h-9 bg-white sm:w-[150px]" data-testid="audit-date-from" />
        <Input type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} className="h-9 bg-white sm:w-[150px]" data-testid="audit-date-to" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        {loading && !data && <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />}
        {data?.length === 0 && <p className="text-sm text-slate-500" data-testid="audit-empty">No changes match these filters.</p>}
        <ul className="space-y-4" data-testid="audit-list">{(data || []).map((e) => <AuditEntry key={e.id} e={e} showBooking />)}</ul>
        {data?.length === 500 && <p className="mt-4 text-xs text-slate-400">Showing the latest 500 entries – narrow the filters to see more.</p>}
      </div>
    </div>
  );
}
